import { Check, Sparkles, Share2, Clock, BookOpen } from "lucide-react";

/**
 * KeyTakeawaysCard — infographic that summarises a blog post
 * into a bite-sized takeaway card. Auto-generates 5 takeaways
 * from the blog's section headings if `takeaways` prop is not provided.
 */
export const KeyTakeawaysCard = ({ post }) => {
  // If explicit takeaways given, use them; else derive from headings + first paragraphs
  const derived = (post.sections || [])
    .filter((s) => s.type === "heading")
    .slice(0, 5)
    .map((h) => h.text);
  const takeaways = post.keyTakeaways && post.keyTakeaways.length > 0 ? post.keyTakeaways : derived;

  if (!takeaways.length) return null;

  return (
    <div className="my-10 rounded-3xl overflow-hidden shadow-xl border border-orange-100" data-testid="key-takeaways-card">
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-950 via-red-950 to-amber-950 text-white p-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-8 -left-8 w-40 h-40 border-2 border-yellow-300 rounded-full" />
          <div className="absolute -bottom-10 -right-10 w-52 h-52 border-2 border-yellow-300 rounded-full" />
        </div>
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-yellow-200" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-yellow-200 uppercase tracking-widest">Key Takeaways</p>
            <h3 className="font-display text-2xl font-bold">In a Nutshell for Parents</h3>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white p-6">
        <ol className="space-y-3">
          {takeaways.map((t, i) => (
            <li key={t} className="flex items-start gap-3 group" data-testid={`takeaway-${i}`}>
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white flex items-center justify-center font-bold text-sm shadow group-hover:scale-110 transition-transform">
                {i + 1}
              </div>
              <p className="text-stone-800 font-medium leading-relaxed pt-1">{t}</p>
            </li>
          ))}
        </ol>
      </div>

      {/* Footer meta strip */}
      <div className="bg-orange-50 px-6 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4 text-xs text-stone-600">
          {post.readTime && (
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
          )}
          {post.category && (
            <span className="inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> {post.category}</span>
          )}
        </div>
        <button
          onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: post.title,
                text: `Read: ${post.title} — Aadi Gayanam Play School`,
                url: window.location.href
              }).catch(() => {});
            } else if (navigator.clipboard) {
              navigator.clipboard.writeText(window.location.href);
            }
          }}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-700 hover:text-orange-900"
          data-testid="share-blog"
        >
          <Share2 className="w-3.5 h-3.5" /> Share with a Parent Friend
        </button>
      </div>
    </div>
  );
};

/**
 * BlogMetaInfographic — shows read time / word count / category / age relevance
 * as a small stat card at the top of a blog article.
 */
export const BlogMetaInfographic = ({ post }) => {
  const wordCount = (post.sections || [])
    .filter((s) => s.type === "paragraph" || s.type === "heading" || s.type === "subheading")
    .reduce((sum, s) => sum + (s.text ? s.text.split(/\s+/).length : 0), 0);

  const stats = [
    { label: "Read Time", value: post.readTime || "5 min", color: "from-orange-500 to-red-500" },
    { label: "Words", value: `${wordCount > 100 ? Math.round(wordCount / 100) * 100 : wordCount}+`, color: "from-amber-500 to-orange-500" },
    { label: "Category", value: post.category || "General", color: "from-rose-500 to-pink-600" },
    { label: "For", value: "Every Parent", color: "from-emerald-500 to-teal-600" }
  ];

  return (
    <div className="my-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="blog-meta-infographic">
      {stats.map((s) => (
        <div key={s.label} className="rounded-2xl bg-white border border-orange-100 shadow-sm p-3 text-center hover:shadow-md transition-all">
          <div className={`inline-block w-8 h-1 rounded-full bg-gradient-to-r ${s.color} mb-2`} />
          <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider">{s.label}</p>
          <p className="font-display text-lg font-bold text-stone-800">{s.value}</p>
        </div>
      ))}
    </div>
  );
};

export default KeyTakeawaysCard;
